import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, FileText, HelpCircle, XCircle, Download, Clock, History, RefreshCw } from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  awaiting_counsellor_review: { label: "Pending review", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  approved:                   { label: "Approved",       tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  info_requested:             { label: "Info requested", tone: "bg-sky-50 text-sky-800 border-sky-200" },
  declined:                   { label: "Declined",       tone: "bg-rose-50 text-rose-800 border-rose-200" },
};

const ACTION_META = {
  approve:      { label: "Approve",         icon: CheckCircle2, tone: "bg-emerald-700 hover:bg-emerald-800", prompt: "Add a note the user will see — e.g. what's safe to act on and what still needs clarification." },
  request_info: { label: "Request more info", icon: HelpCircle, tone: "bg-sky-700 hover:bg-sky-800",       prompt: "Tell the user exactly what's missing (e.g. missing panel, legible scan, consent page)." },
  decline:      { label: "Decline",         icon: XCircle,      tone: "bg-rose-700 hover:bg-rose-800",       prompt: "Explain why this cannot be reviewed (wrong document, poor quality, outside scope)." },
};

const fmtSize = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function AdminGenome() {
  const [filter, setFilter] = useState("awaiting_counsellor_review");
  const [data, setData] = useState({ reports: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);   // selected report for the dialog
  const [action, setAction] = useState(null);   // "approve" | "request_info" | "decline"
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/genome/admin/reports", { params: { status: filter } });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openAction = (report, kind) => {
    setActive(report);
    setAction(kind);
    setNote("");
  };
  const closeDialog = () => { setActive(null); setAction(null); setNote(""); };

  const submit = async () => {
    if (!active || !action) return;
    const trimmed = note.trim();
    if (!trimmed) { toast.error("A reviewer note is required"); return; }
    setPosting(true);
    try {
      await api.post(`/genome/admin/reports/${active.id}/decision`, { action, note: trimmed });
      toast.success(`Report ${ACTION_META[action].label.toLowerCase()}`);
      closeDialog();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const downloadReport = (report) => {
    // Open in new tab — API uses cookie/jwt auth already present.
    const url = `${API_BASE}/genome/admin/reports/${report.id}/download`;
    window.open(url, "_blank", "noopener");
  };

  const totals = useMemo(() => ({
    pending: data.counts?.awaiting_counsellor_review || 0,
    approved: data.counts?.approved || 0,
    info: data.counts?.info_requested || 0,
    declined: data.counts?.declined || 0,
  }), [data]);

  return (
    <AppShell
      pageTitle="GenomePramana · Counsellor review"
      pageDescription="Every genomic upload lands here. Approve, request more info, or decline — every action is logged with a note the user will see."
      actions={
        <Button variant="outline" onClick={load} data-testid="genome-admin-refresh">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: "pending",  l: "Pending" },
          { k: "approved", l: "Approved" },
          { k: "info",     l: "Info requested" },
          { k: "declined", l: "Declined" },
        ].map((c) => (
          <div key={c.k} className="rounded-lg border border-border bg-white p-4" data-testid={`genome-admin-kpi-${c.k}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.l}</div>
            <div className="mt-2 font-serif text-2xl text-primary">{totals[c.k]}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">Filter</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-56" data-testid="genome-admin-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="awaiting_counsellor_review">Pending review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="info_requested">Info requested</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : data.reports.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-serif text-primary text-lg">Nothing in this queue</div>
            <p className="mt-1 text-sm text-muted-foreground">All caught up. Users will land here as uploads arrive.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data.reports.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, tone: "" };
              return (
                <li key={r.id} className="p-4" data-testid={`genome-admin-row-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-primary" />
                        <span className="text-sm text-primary truncate max-w-xs">{r.filename}</span>
                        <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>{meta.label}</Badge>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span>{r.user_name || r.user_email || r.user_id}</span>
                        <span>· {new Date(r.created_at).toLocaleString()}</span>
                        <span>· {fmtSize(r.size_bytes)}</span>
                        <span>· {r.content_type || "unknown"}</span>
                      </div>
                      {r.notes ? <div className="mt-2 text-xs text-muted-foreground italic">"{r.notes}"</div> : null}
                      {r.review_history?.length ? (
                        <details className="mt-2 text-xs text-muted-foreground">
                          <summary className="cursor-pointer inline-flex items-center gap-1"><History size={11} /> {r.review_history.length} review action{r.review_history.length > 1 ? "s" : ""}</summary>
                          <ul className="mt-2 space-y-1 border-l pl-3 border-border">
                            {r.review_history.map((h, i) => (
                              <li key={i}>
                                <span className="text-primary">{h.status_after}</span>
                                <span> · {h.reviewer_name}</span>
                                <span> · {new Date(h.at).toLocaleString()}</span>
                                {h.note ? <div className="text-muted-foreground">"{h.note}"</div> : null}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => downloadReport(r)} data-testid={`genome-admin-download-${r.id}`}>
                        <Download size={13} className="mr-1" /> Open
                      </Button>
                      {r.status === "awaiting_counsellor_review" || r.status === "info_requested" ? (
                        <>
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => openAction(r, "approve")} data-testid={`genome-admin-approve-${r.id}`}>
                            <CheckCircle2 size={13} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" className="bg-sky-700 hover:bg-sky-800" onClick={() => openAction(r, "request_info")} data-testid={`genome-admin-info-${r.id}`}>
                            <HelpCircle size={13} className="mr-1" /> Request info
                          </Button>
                          <Button size="sm" className="bg-rose-700 hover:bg-rose-800" onClick={() => openAction(r, "decline")} data-testid={`genome-admin-decline-${r.id}`}>
                            <XCircle size={13} className="mr-1" /> Decline
                          </Button>
                        </>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock size={11} /> {r.last_reviewed_at ? new Date(r.last_reviewed_at).toLocaleDateString() : "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={!!active && !!action} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent data-testid="genome-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-primary text-xl">
              {action ? ACTION_META[action].label : ""} · {active?.filename}
            </DialogTitle>
            <DialogDescription>{action ? ACTION_META[action].prompt : ""}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            placeholder="Add a note the user will see…"
            data-testid="genome-admin-note"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={posting || !note.trim()}
              className={action ? ACTION_META[action].tone : ""}
              data-testid="genome-admin-confirm"
            >
              {posting ? "Saving…" : `Confirm ${action ? ACTION_META[action].label : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
